import i18next, { t } from "i18next";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { GeneralButton } from "../../../../components";
import { EndPoints } from "../../../../Constants/endpoints";
import RecordCardItem, {
  RecordCardType,
} from "../../component/RecordCardItem/RecordCardItem";
import MedicalRecordHeader from "../../MedicalRecordHeader/MedicalRecordHeader";
import PatientMedicalRecordBody from "./PatientMedicalRecordBody";
import PrintIcon from "../../../../assets/images/icons/ion_print-outline.svg";
import { useAppMediaQuery, useGetData, usePrintPdf } from "../../../../hooks";
import { handlePrint } from "../../../../utils/fileUtils";

const PatientMedicalRecordDetails = () => {
  const [openItemId, setOpenItemId] = useState<number>();
  const [isExportPDF, setIsExportPDF] = useState<boolean>(false);
  const [isPrinting, setIsPrinting] = useState(false);

  const { id: PatientId, recordId } = useParams();
  const navigate = useNavigate();
  const { isMobileOrTablet } = useAppMediaQuery();
  const GetMedicalRecord = useGetData<any>(
    EndPoints.GetMedicalRecord,
    {},
    recordId,
  );
  const { data } = GetMedicalRecord;

  const {
    handleExportPDF,
    isLoading: isLoadingExport,
    isSuccess,
    data: fileData,
  } = usePrintPdf({
    endPoint: `${EndPoints.GenerateMedicalRecordWithFollowUpsPdf}`,
    id: Number(recordId),
    enabled: isExportPDF || isPrinting,
  });

  useEffect(() => {
    if (isSuccess && isExportPDF) {
      handleExportPDF().then(() => {
        setIsExportPDF(false);
      });
    }
  }, [isSuccess, isExportPDF]);

  useEffect(() => {
    if (isSuccess && isPrinting) {
      handlePrint(fileData?.Data?.FileContents, "PDF", setIsPrinting);
    }
  }, [isSuccess, isPrinting]);

  return (
    <div
      className="box relative mx-auto h-[100dvh] w-full overflow-auto !pt-8"
      dir={i18next.language?.slice(0, 2) == "en" ? "ltr" : "rtl"}
    >
      <div className="fixed flex w-[94vw] justify-between bg-white pb-3 max-md:flex-wrap">
        <MedicalRecordHeader
          selectedTemplateName={data?.Data?.MedicalHistoryType?.Name}
          title={t("view_medical_report")}
        />
        <div className="mt-4 flex justify-end gap-2">
          <GeneralButton
            color="lite"
            onClick={() => setIsExportPDF(true)}
            isLoading={isExportPDF}
            title={isMobileOrTablet ? undefined : t("export_as_pdf")}
            className="border-body-text !text-primary-text border !bg-white"
            icon={isMobileOrTablet ? PrintIcon : undefined}
          />
          <GeneralButton
            color="lite"
            icon={PrintIcon}
            onClick={() => setIsPrinting(true)}
            title={t("print")}
            className="border border-primary"
            isLoading={isPrinting}
          />

          <GeneralButton
            className=""
            onClick={() =>
              navigate(
                `/patients_list/${PatientId}/medical-records/${recordId}/new-followup`,
              )
            }
            title={
              isMobileOrTablet ? t("add_follow_up") : t("add_new_follow_up")
            }
          />
        </div>
      </div>

      <div className="mt-10 flex gap-2 overflow-auto pb-2 max-lg:flex-col lg:gap-12">
        <div className="lg:w-[62%]">
          <p className="mt-8 pb-2 font-semibold">{t("last_medical_report")}</p>
          <PatientMedicalRecordBody GetMedicalRecord={GetMedicalRecord} />
        </div>
        {data?.Data?.Visits && (
          <div className="flex max-h-[calc(100dvh-5rem)] flex-1 flex-col gap-2 overflow-auto pb-3">
            <p className="mt-8 pb-2 font-semibold">{t("follow_ups")}</p>
            {data?.Data?.Visits?.map((item: any, index: number) => {
              return (
                <RecordCardItem
                  item={item}
                  key={index}
                  openItemId={openItemId}
                  setOpenItemId={setOpenItemId}
                  type={RecordCardType.FollowUp}
                />
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default PatientMedicalRecordDetails;
